import React from 'react';

/**
 * Renderiza el selector de vista con los botones "Guardar" o "Guardar Nuevo"
 * O el input para guardar cuando showSaveAsInput es true
 */
export function renderViewSelector({
    activeViewId,
    viewSets,
    viewSelectorLabel,
    customViewLabel,
    saveAsNewLabel,
    saveViewLabel,
    updateViewLabel,
    hasActiveView,
    hasUnsavedChanges,
    showSaveAsInput,
    newSetName,
    isSaving,
    setNamePlaceholder,
    onViewSelect,
    onSaveAsNew,
    onSaveView,
    onUpdateView,
    onNameChange,
    onCancel,
    t
}) {
    // Si está en modo guardar, mostrar input en lugar del selector
    if (showSaveAsInput) {
        return (
            <div className="columns-selector-modal__view-selector">
                <input
                    type="text"
                    className="form-control columns-selector-modal__save-input"
                    placeholder={setNamePlaceholder}
                    value={newSetName}
                    onChange={onNameChange}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter' && newSetName.trim() && !isSaving) {
                            onSaveView(e);
                        }
                    }}
                />
                <button 
                    type="button"
                    className="btn columns-selector-modal__save-view-btn"
                    onClick={onSaveView}
                    disabled={!newSetName.trim() || isSaving}
                >
                    {isSaving ? t('common.saving', 'Guardando...') : t('common.save', 'Guardar')}
                </button>
                <button 
                    type="button"
                    className="btn -alt columns-selector-modal__cancel-save-btn"
                    onClick={onCancel}
                    disabled={isSaving}
                >
                    {t('common.cancel', 'Cancelar')}
                </button>
            </div>
        );
    }

    // Modo normal: mostrar selector y botones
    return (
        <div className="columns-selector-modal__view-selector">
            <label className="columns-selector-modal__view-label">
                {viewSelectorLabel}:
            </label>
            <select 
                value={activeViewId}
                onChange={onViewSelect}
                className="form-select columns-selector-modal__view-select"
            >
                <option value="custom">{customViewLabel}</option>
                {viewSets.map(set => {
                    const displayName = set.displayName || set.name;
                    return (
                        <option key={set.id} value={set.id.toString()}>
                            {displayName}
                        </option>
                    );
                })}
            </select>
            {hasActiveView && hasUnsavedChanges && (
                <button 
                    type="button"
                    className="btn columns-selector-modal__update-view-btn"
                    onClick={onUpdateView}
                    disabled={isSaving}
                >
                    {isSaving ? t('common.saving', 'Guardando...') : updateViewLabel}
                </button>
            )}
            {hasActiveView ? (
                <button 
                    type="button"
                    className="btn -alt columns-selector-modal__save-as-new-btn"
                    onClick={onSaveAsNew}
                >
                    {saveAsNewLabel}
                </button>
            ) : (
                <button 
                    type="button"
                    className="btn columns-selector-modal__save-view-btn"
                    onClick={onSaveAsNew}
                >
                    {saveViewLabel}
                </button>
            )}
        </div>
    );
}

/**
 * Renderiza el input para guardar como nuevo
 */
export function renderSaveAsInput({
    showSaveAsInput,
    newSetName,
    isSaving,
    placeholder,
    onNameChange,
    onSave,
    t
}) {
    if (!showSaveAsInput) return null;

    return (
        <div className="columns-selector-modal__save-input-group">
            <input
                type="text"
                className="form-control columns-selector-modal__save-input"
                placeholder={placeholder}
                value={newSetName}
                onChange={onNameChange}
                onKeyPress={(e) => {
                    if (e.key === 'Enter' && newSetName.trim() && !isSaving) {
                        onSave(e);
                    }
                }}
            />
            <button 
                type="button"
                className="btn -alt columns-selector-modal__save-btn"
                onClick={onSave}
                disabled={!newSetName.trim() || isSaving}
            >
                {isSaving ? t('common.saving', 'Guardando...') : t('common.save', 'Guardar')}
            </button>
        </div>
    );
}

/**
 * Renderiza los botones de acción contextuales
 */
export function renderActionButtons({
    hasActiveView,
    hasUnsavedChanges,
    showSaveAsInput,
    newSetName,
    isSaving,
    saveViewLabel,
    updateViewLabel,
    setNamePlaceholder,
    onSaveAsNew,
    onSaveView,
    onUpdateView,
    onApply,
    onClose,
    onNameChange,
    t
}) {
    const saveAsInputProps = {
        showSaveAsInput,
        newSetName,
        isSaving,
        placeholder: setNamePlaceholder,
        onNameChange,
        onSave: onSaveView,
        t
    };

    return (
        <div className="columns-selector-modal__action-buttons">
            {/* CASO 1: Vista desde cero (Vista Personalizada) */}
            {!hasActiveView && !showSaveAsInput && (
                <>
                    <button onClick={onApply} className="btn -primary">
                        {t('common.apply', 'Aplicar')}
                    </button>
                </>
            )}

            {/* CASO 2: Vista cargada SIN modificar */}
            {hasActiveView && !hasUnsavedChanges && !showSaveAsInput && (
                <>
                    <button onClick={onApply} className="btn -primary">
                        {t('common.apply', 'Aplicar')}
                    </button>
                </>
            )}

            {/* CASO 3: Vista cargada CON modificaciones */}
            {hasActiveView && hasUnsavedChanges && !showSaveAsInput && (
                <>
                    <button onClick={onApply} className="btn -primary">
                        {t('common.apply', 'Aplicar')}
                    </button>
                </>
            )}

            <button onClick={onClose} className="btn -alt">
                {t('common.cancel', 'Cancelar')}
            </button>
        </div>
    );
}

/**
 * Renderiza la barra de búsqueda
 */
export function renderSearchBar({
    searchQuery,
    placeholder,
    onSearchChange
}) {
    return (
        <div className="columns-selector-modal__search-bar">
            <input
                type="text"
                className="form-control columns-selector-modal__search-input"
                placeholder={placeholder}
                value={searchQuery}
                onChange={onSearchChange}
            />
        </div>
    );
}

/**
 * Renderiza el mensaje de error
 */
export function renderError(errorMessage) {
    if (!errorMessage) return null;

    return (
        <div className="columns-selector-modal__error-message">
            {errorMessage}
        </div>
    );
}

